/**
 * Prompt Builder — the prompts future LLMs will receive.
 *
 * Builds a provider-agnostic request: system message (role + rules), user
 * message (the raw prompt plus any admin hints), and the function schema
 * the model must call. Vendors differ in envelope, not content — adapters
 * reshape this object, they don't rewrite prompts.
 */

import { buildCreativeBriefFunction } from './functionSchema';
import { listAudiences, listMoods, listBrandStyles } from '../aiCreativeDirector';

const SYSTEM_PROMPT = [
  'You are the creative brief writer for iVisora, a professional flyer design system.',
  'Read the user request and choose a creative brief by calling decide_creative_brief.',
  'Rules:',
  '- Choose the single best-fitting audience; never invent values outside the enums.',
  '- Only pin mood or brandStyle when the request clearly implies them; otherwise omit them.',
  '- You make decisions only. You never produce SVG, HTML, code, or design geometry.',
].join('\n');

/**
 * Build the request for a creative-brief decision.
 * hints = { audience?, mood?, brandStyle?, category? } — admin-pinned values
 * the model must respect (they are restated in the user message AND enforced
 * downstream regardless of what the model returns).
 */
export const buildCreativeBriefPrompt = (userPrompt, hints = {}) => {
  const hintLines = Object.entries(hints)
    .filter(([, v]) => v)
    .map(([k, v]) => `The admin has pinned ${k} = "${v}". Do not override it.`);

  return {
    system: SYSTEM_PROMPT,
    user: [
      `Design request: ${userPrompt || '(no prompt — choose sensible defaults)'}`,
      ...hintLines,
    ].join('\n'),
    functions: [buildCreativeBriefFunction()],
    functionCall: { name: 'decide_creative_brief' },
    vocabulary: {
      audiences: listAudiences().map((a) => a.id),
      moods: listMoods().map((m) => m.id),
      brandStyles: listBrandStyles().map((s) => s.id),
    },
  };
};
